// @flow
import { useState, useMemo, useRef } from 'react';
import sortedIndexBy from 'lodash/sortedIndexBy';
import { getPageContext, getPageLocale } from 'components/PageContext';
import type { BlokComponentType } from 'components/types';
import { getTranslation } from '@ts-react-utils/i18n';
import type { CmsShopType, TCApiShopType } from './types';
import { ApiShopsProvider } from './apiShopsContext';
import { GeoLocationProvider } from './geoLocationContext';
import Filters from './Filters';
import renderComponent from '../renderComponent';
import componentKeys from '../../../componentKeys';
import ShopCard from './ShopCard';
import ShopCardFallback from './ShopCardFallback';
import { ShopListGrid, ShopListHeading } from './styled';

type PropsType = {
  blok: {
    areas_label?: string,
    cached_api_shops?: { [string]: TCApiShopType },
    default_shop_reserve_label: string,
    header?: string,
    names_label?: string,
    shops: BlokComponentType<CmsShopType>[],
    tags_label?: string
  }
};

function insertOption(
  options: { label: string, value: string }[],
  newOption: string
) {
  if (!newOption) return;
  const option = {
    label: newOption,
    value: newOption.toLowerCase().trim()
  };
  if (!options.length) {
    options.push(option);
    return;
  }
  const insertTagIndex = sortedIndexBy(options, option, ({ value }) => value);
  if (
    !options[insertTagIndex] ||
    options[insertTagIndex].value !== option.value
  ) {
    options.splice(insertTagIndex, 0, option);
  }
}

const ShopList = ({
  blok: {
    shops,
    tags_label: tagsLabel,
    areas_label: areasLabel,
    names_label: namesLabel,
    cached_api_shops: apiShops = {},
    default_shop_reserve_label: defaultReserveLabel,
    header
  }
}: PropsType) => {
  const apiShopsRef = useRef<{ [string]: TCApiShopType }>(apiShops);
  const currentLocationRef = useRef();
  const [filteredShops, setFilteredShops] = useState(shops.slice());

  const filterShops = (selectedAreas, selectedTags, selectedNames) => {
    // we need this to make sure we don't accidentally change the original order
    let result = shops.slice();
    if (selectedAreas.length || selectedTags.length || selectedNames.length) {
      result = shops.filter((shop) => {
        let hasMatchingTag = true;
        if (selectedTags.length) {
          hasMatchingTag = (shop.tags || []).reduce(
            (hasTag, tag) =>
              hasTag ||
              selectedTags.find(
                (selectedTag) => selectedTag === tag.toLowerCase().trim()
              ),
            false
          );
        }
        let hasMatchingArea = true;
        if (selectedAreas.length) {
          hasMatchingArea = selectedAreas.find(
            (selectedArea) => selectedArea === shop.area.toLowerCase().trim()
          );
        }
        let hasMatchingName = true;
        if (selectedNames.length) {
          hasMatchingName = selectedNames.find(
            (selectedName) => selectedName === shop.slug.toLowerCase().trim()
          );
        }
        return hasMatchingArea && hasMatchingTag && hasMatchingName;
      });
    }
    setFilteredShops(result);
  };

  const {
    story: { full_slug: storySlug }
  } = getPageContext();
  const path = storySlug.split('/').slice(-2, -1)[0];

  const locale = getPageLocale();

  const [tagOptions, areaOptions, nameOptions] = useMemo(
    () =>
      shops.reduce(
        (optionsTuple, shop) => {
          (shop.tags || []).forEach((tag) =>
            insertOption(optionsTuple[0], tag)
          );
          if (shop.area) {
            insertOption(optionsTuple[1], shop.area);
          }
          if (namesLabel && apiShopsRef.current[shop.slug]) {
            optionsTuple[2].push({
              label: getTranslation(
                apiShopsRef.current[shop.slug].name_translations,
                locale
              ),
              value: shop.slug
            });
          }
          return optionsTuple;
        },
        [[], [], []]
      ),
    [shops]
  );
  const shopSlugs = useMemo(() => shops.map(({ slug }) => slug), [shops]);

  return (
    <GeoLocationProvider currentLocationRef={currentLocationRef}>
      <ApiShopsProvider slugs={shopSlugs} apiShopsRef={apiShopsRef}>
        {header && <ShopListHeading richText={header} />}
        <Filters
          tagOptions={tagOptions}
          areaOptions={areaOptions}
          nameOptions={nameOptions}
          areasLabel={areasLabel}
          tagsLabel={tagsLabel}
          namesLabel={namesLabel}
          filterShops={filterShops}
        />
        <ShopListGrid>
          {filteredShops.map((shop) => {
            const hasDinerApiData = !!apiShopsRef.current[shop.slug];
            const showFallbackShopCards =
              CONFIG.appVersion === 'development' ||
              CONFIG.publicUrl === 'https://staging-snap.tablecheck.com';
            if (hasDinerApiData) {
              return renderComponent(
                shop,
                {
                  customPath: 'components/editable/ShopsList/index.jsx',
                  key: shop._uid,
                  utmSource: path,
                  defaultReserveLabel,
                  storySlug
                },
                {
                  [componentKeys.SHOP]: ShopCard
                }
              );
            }
            if (showFallbackShopCards) {
              return renderComponent(
                shop,
                {
                  customPath: 'components/editable/ShopsList/index.jsx',
                  key: shop._uid,
                  utmSource: path,
                  defaultReserveLabel,
                  storySlug
                },
                {
                  [componentKeys.SHOP]: ShopCardFallback
                }
              );
            }
            return null;
          })}
        </ShopListGrid>
      </ApiShopsProvider>
    </GeoLocationProvider>
  );
};

export default ShopList;
